import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import {
  fetchPersonnel,
  PERSONNEL_PAGE_SIZE,
  PersonnelListResponse,
  PersonnelStatus,
} from "services/personnel.service";
import { fetchDepartments } from "services/departments.service";
import { fetchPositions } from "services/positions.service";
import { useRestorableState } from "hooks/useRestorableState";
import UserAvatar from "components/user-avatar/user-avatar";
import PersonalasTabs from "features/positions/PersonalasTabs";
import { genderedTitle } from "features/positions/positions-helpers";
import PersonnelChips from "./PersonnelChips";
import { departmentsFromRows, flattenDepartments, formatDay } from "./personnel-helpers";

interface ListFilters {
  status: PersonnelStatus;
  search: string;
  departmentId: string;
  positionId: string;
  /** «Be įdarbinimo įrašo» toggle. Absent on state restored before this field existed — default off. */
  noOnboardingRecord?: boolean;
  page: number;
}

const INITIAL_FILTERS: ListFilters = {
  status: "active",
  search: "",
  departmentId: "",
  positionId: "",
  noOnboardingRecord: false,
  page: 1,
};

type Option = { id: string; name: string };

/**
 * Personalas → «Darbuotojai» (route /app/:companyId/personnel): one row per
 * employee the viewer may see (the backend scopes it — Master / Access
 * onboarding / department scoping / heads with data access). Row → the
 * «darbuotojo kortelė». Filters survive Back (useRestorableState).
 */
export default function PersonnelListPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filters, setFilters] = useRestorableState<ListFilters>("personnel-filters", INITIAL_FILTERS);
  const [searchInput, setSearchInput] = useState(filters.search);
  const [data, setData] = useState<PersonnelListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [departmentOptions, setDepartmentOptions] = useState<Option[] | null>(null);
  const [rowDepartments, setRowDepartments] = useState<Option[]>([]);
  const [positionOptions, setPositionOptions] = useState<Option[]>([]);
  const requestRef = useRef(0);
  const debounceRef = useRef<number | undefined>(undefined);
  // The search this input last wrote into the filters — anything else in
  // filters.search came from outside (restored state of another path).
  const writtenSearchRef = useRef(filters.search);

  const update = (patch: Partial<ListFilters>) =>
    setFilters((previous) => ({ ...previous, page: 1, ...patch }));

  // Debounced search: the input is local, the filter follows after a pause.
  useEffect(() => {
    if (searchInput === filters.search) return undefined;
    debounceRef.current = window.setTimeout(() => {
      writtenSearchRef.current = searchInput;
      update({ search: searchInput });
    }, 300);
    return () => window.clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // The stored search changed from outside: show it in the box.
  useEffect(() => {
    if (filters.search === writtenSearchRef.current) return;
    writtenSearchRef.current = filters.search;
    setSearchInput(filters.search);
  }, [filters.search]);

  // Another company: its own stored filters apply. Drop a pending search of
  // the previous company (it would land in this company's filters) and
  // everything collected for it.
  useEffect(() => {
    window.clearTimeout(debounceRef.current);
    writtenSearchRef.current = filters.search;
    setSearchInput(filters.search);
    setData(null);
    setLoadFailed(false);
    setRowDepartments([]);
    setDepartmentOptions(null);
    setPositionOptions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    fetchPersonnel(companyId, {
      status: filters.status,
      search: filters.search,
      departmentId: filters.departmentId || null,
      positionId: filters.positionId || null,
      onboardingRecord: filters.noOnboardingRecord ? "missing" : null,
      page: filters.page,
      limit: PERSONNEL_PAGE_SIZE,
    })
      .then((result) => {
        if (requestId !== requestRef.current) return;
        setData(result);
        setLoadFailed(false);
        setRowDepartments((known) => departmentsFromRows(result.rows, known));
      })
      .catch(() => {
        if (requestId === requestRef.current) setLoadFailed(true);
      })
      .finally(() => {
        if (requestId === requestRef.current) setLoading(false);
      });
  }, [
    companyId,
    filters.status,
    filters.search,
    filters.departmentId,
    filters.positionId,
    filters.noOnboardingRecord,
    filters.page,
    reloadKey,
  ]);

  // Filter options. The departments list needs DEPARTMENTS_VIEW_GROUPS; a head
  // without them gets the departments seen on the rows instead.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    fetchDepartments(companyId)
      .then((departments) => {
        if (!cancelled) setDepartmentOptions(flattenDepartments(Array.isArray(departments) ? departments : []));
      })
      .catch(() => {
        if (!cancelled) setDepartmentOptions(null);
      });
    fetchPositions(companyId)
      .then((positions) => {
        if (!cancelled)
          setPositionOptions(
            positions
              .map((position) => ({ id: position.id, name: genderedTitle(position) }))
              .sort((a, b) => a.name.localeCompare(b.name, "lt"))
          );
      })
      .catch(() => {
        if (!cancelled) setPositionOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const departments = useMemo(() => departmentOptions ?? rowDepartments, [departmentOptions, rowDepartments]);

  if (!companyId) return null;

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const limit = data?.limit || PERSONNEL_PAGE_SIZE;
  const page = filters.page;
  const pageCount = Math.max(1, Math.ceil(total / limit));
  // The «Išėjimo data» column only makes sense for people who left.
  const showQuitDate = filters.status === "left";
  const openCard = (userId: string) => navigate(`/app/${companyId}/personnel/${userId}`);

  const statusButton = (status: PersonnelStatus, label: string) => (
    <button
      type="button"
      aria-pressed={filters.status === status}
      onClick={() => update({ status })}
      className={clsx(
        "px-3 py-1.5 text-sm font-medium first:rounded-l-lg last:rounded-r-lg max-md:min-h-11",
        filters.status === status ? "bg-blue-600 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
      )}
    >
      {label}
    </button>
  );

  const selectClass =
    "w-full max-w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-8 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 sm:w-auto md:text-sm";

  return (
    <div className="w-full px-6 py-4">
      <PersonalasTabs companyId={companyId} />
      <div className="mb-4">
        <h1 className="text-2xl font-semibold">{t("hr_module")}</h1>
        <p className="mt-0.5 text-sm text-gray-500">{t("personnel_list_hint")}</p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-3 p-4">
          <label className="relative block w-full min-w-0 flex-1 sm:w-auto sm:min-w-[16rem]">
            <Search aria-hidden className="pointer-events-none absolute left-2 top-3 h-4 w-4 text-gray-400 md:top-2.5" />
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={t("personnel_search_placeholder")}
              aria-label={t("search")}
              className="w-full rounded-lg border border-gray-300 py-2 pl-8 pr-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 md:text-sm"
            />
          </label>
          <div
            role="group"
            aria-label={t("personnel_status_filter")}
            className="inline-flex divide-x divide-gray-300 overflow-hidden rounded-lg border border-gray-300"
          >
            {statusButton("active", t("personnel_status_active"))}
            {statusButton("left", t("personnel_status_left"))}
          </div>
          {departments.length > 0 && (
            <select
              aria-label={t("departments")}
              className={selectClass}
              value={filters.departmentId}
              onChange={(event) => update({ departmentId: event.target.value })}
            >
              <option value="">{t("personnel_all_departments")}</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </select>
          )}
          {positionOptions.length > 0 && (
            <select
              aria-label={t("positions_title")}
              className={selectClass}
              value={filters.positionId}
              onChange={(event) => update({ positionId: event.target.value })}
            >
              <option value="">{t("personnel_all_positions")}</option>
              {positionOptions.map((position) => (
                <option key={position.id} value={position.id}>
                  {position.name}
                </option>
              ))}
            </select>
          )}
          <label className="inline-flex w-full items-center gap-2 text-sm text-gray-700 max-md:min-h-11 sm:w-auto">
            <input
              type="checkbox"
              checked={!!filters.noOnboardingRecord}
              onChange={(event) => update({ noOnboardingRecord: event.target.checked })}
              className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 md:h-4 md:w-4"
            />
            {t("personnel_filter_no_onboarding_record")}
          </label>
        </div>

        {loadFailed ? (
          <div className="flex items-center gap-3 px-4 pb-4 text-sm text-red-700">
            {t("personnel_load_failed")}
            <button type="button" className="underline" onClick={() => setReloadKey((key) => key + 1)}>
              {t("retry")}
            </button>
          </div>
        ) : loading && !data ? (
          <p className="px-4 pb-4 text-sm text-gray-500">{t("loading")}</p>
        ) : rows.length === 0 ? (
          <p className="px-4 pb-4 text-sm text-gray-500">{t("personnel_empty")}</p>
        ) : (
          <div className={clsx("overflow-x-auto", loading && "opacity-60")}>
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2">{t("employee")}</th>
                  <th className="hidden px-4 py-2 md:table-cell">{t("positions_title")}</th>
                  <th className="hidden px-4 py-2 md:table-cell">{t("departments")}</th>
                  <th className="hidden px-4 py-2 md:table-cell">{t("manager")}</th>
                  <th className="hidden px-4 py-2 text-right md:table-cell">{t("hiring_date")}</th>
                  {showQuitDate && <th className="px-4 py-2 text-right">{t("quit_date")}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr
                    key={row.userId}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => openCard(row.userId)}
                  >
                    <td className="px-4 py-2 align-top">
                      <div className="flex items-start gap-3">
                        <UserAvatar
                          companyId={companyId}
                          user={{ id: row.userId, name: row.name, avatarFileId: row.avatarFileId }}
                          size={32}
                          rounded="full"
                        />
                        <div className="min-w-0">
                          <button
                            type="button"
                            className="text-left font-medium text-gray-900 hover:text-blue-700"
                            onClick={(event) => {
                              event.stopPropagation();
                              openCard(row.userId);
                            }}
                          >
                            {row.name || "—"}
                          </button>
                          {/* Phone: the position and department columns are hidden, so they sit under the name. */}
                          <p className="text-xs text-gray-500 md:hidden">
                            {[row.positionsText, row.departments.map((department) => department.name).join(", ")]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                          <PersonnelChips chips={row.chips} />
                        </div>
                      </div>
                    </td>
                    <td className="hidden px-4 py-2 align-top text-gray-700 md:table-cell">{row.positionsText || "—"}</td>
                    <td className="hidden px-4 py-2 align-top text-gray-700 md:table-cell">
                      {row.departments.map((department) => department.name).join(", ") || "—"}
                    </td>
                    <td className="hidden px-4 py-2 align-top text-gray-700 md:table-cell">{row.manager?.name || "—"}</td>
                    <td className="hidden whitespace-nowrap px-4 py-2 text-right align-top tabular-nums text-gray-700 md:table-cell">
                      {formatDay(row.hiringDate) || "—"}
                    </td>
                    {showQuitDate && (
                      <td className="whitespace-nowrap px-4 py-2 text-right align-top tabular-nums text-gray-700">
                        {formatDay(row.quitDate) || "—"}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > limit && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2 text-sm text-gray-600">
            <span>
              {t("personnel_pagination", {
                from: (page - 1) * limit + 1,
                to: Math.min(page * limit, total),
                total,
              })}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="inline-flex items-center rounded-md p-1 hover:bg-gray-100 disabled:opacity-40 max-md:p-3"
                disabled={page <= 1 || loading}
                onClick={() => setFilters((previous) => ({ ...previous, page: previous.page - 1 }))}
                aria-label={t("personnel_page_previous")}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                className="inline-flex items-center rounded-md p-1 hover:bg-gray-100 disabled:opacity-40 max-md:p-3"
                disabled={page >= pageCount || loading}
                onClick={() => setFilters((previous) => ({ ...previous, page: previous.page + 1 }))}
                aria-label={t("next")}
              >
                <ChevronRight className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
